/**
 * Fast Choice gameplay module.
 * Exports: renderFastChoice(modal, data, onComplete), teardownFastChoice(modal)
 *
 * Anti-cheat:
 *   - rounds[].options[] contain only opaque side ('a'/'b') and label
 *   - the correct option is never sent to the client
 *   - image URLs are fetched lazily per round via /round/{token}
 *   - feedback overlays (+4/+5/-1) are inserted only AFTER server confirms
 */

import imgGameIcon from '../../images/game-icon.svg';
import { renderGameMessage, startBlockedMessage } from '../shared/game-message';
import imgScorePlus4 from './score-plus-4.svg';
import imgScorePlus5 from './score-plus-5.svg';
import imgScoreMinus1 from './score-minus-1.svg';
import imgSeria from './seria.svg';

export const WELCOME_CONFIG = {
    icon: imgGameIcon,
    title: 'Witaj w Fast Choice!',
    description: '<p>Przygotuj się na szybką grę, która sprawdzi Twoją wiedzę i refleks! ' +
        'Na ekranie pojawią się dwa obrazki oraz pytanie. Twoim zadaniem jest wybranie poprawnej odpowiedzi, zanim skończy się czas.</p>' +
        '<p>Uważaj jednak – masz tylko chwilę na każdą rundę, a gra składa się z 6 rund. Liczy się szybka decyzja i dobra koncentracja!</p>',
    howToPlay: {
        heading: 'Jak grać?',
        body: '<ol>' +
            '<li>Spójrz na pytanie i dwa obrazki widoczne na ekranie.</li>' +
            '<li>Wybierz poprawny obrazek, zanim upłynie czas.</li>' +
            '<li>Odpowiadaj jak najszybciej – szybkie poprawne odpowiedzi dają więcej punktów!</li>' +
            '</ol>',
    },
    scoring: {
        heading: 'Punktacja',
        body: '<ul>' +
            '<li>✅ +4 punkty za poprawną odpowiedź</li>' +
            '<li>⚡ +5 punktów za szybką poprawną odpowiedź poniżej 2,5 sekundy</li>' +
            '<li>❌ -1 punkt za błędną odpowiedź – wynik nie spada poniżej 0</li>' +
            '<li>➖ 0 punktów za brak odpowiedzi w czasie</li>' +
            '</ul>' +
            '<p>Maksymalny wynik to 30 punktów.</p>',
    },
};

const TIMER_WARN_FRAC   = 0.5;
const TIMER_DANGER_FRAC = 0.2;
const FEEDBACK_LOCK_MS  = 1100;
const TIMEOUT_LOCK_MS   = 1200;
const STREAK_THRESHOLD  = 3;

let state         = null;
let sessionToken  = null;
let currentTaskId = null;
let gameStartTime = null;
let raf   = null;
let timers = [];

function contentEl(modal) {
    return modal.querySelector('.game-modal__content');
}

function escHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
}

function setHtml(modal, html) {
    const el = contentEl(modal);
    if (el) el.innerHTML = html;
}

function clearTimers() {
    timers.forEach((t) => clearTimeout(t));
    timers = [];
    if (raf) {
        cancelAnimationFrame(raf);
        raf = null;
    }
}

function closeIconSvg() {
    return `<svg viewBox="0 0 100 97" xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true"><path d="M62.704 48L77.3789 33.3251C80.8737 29.8303 80.8737 24.116 77.3789 20.6211C73.8841 17.1263 68.1697 17.1263 64.6749 20.6211L50 35.296L35.3251 20.6211C31.8303 17.1263 26.1159 17.1263 22.6211 20.6211C19.1263 24.1159 19.1263 29.8302 22.6211 33.3251L37.2962 48L22.6211 62.675C19.1263 66.1698 19.1263 71.8841 22.6211 75.3789C26.1159 78.8737 31.8303 78.8737 35.3251 75.3789L50 60.704L64.6749 75.3789C68.1697 78.8737 73.8841 78.8737 77.3789 75.3789C80.8737 71.8841 80.8737 66.1698 77.3789 62.675L62.704 48Z" fill="currentColor"/></svg>`;
}

async function fetchStart(taskId) {
    const res = await fetch(`/api/task/${taskId}/fast-choice/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
    });
    if (res.status === 409) {
        const err = await res.json();
        throw new Error(err?.error || 'already_completed');
    }
    if (!res.ok) {
        throw new Error(`start_failed_${res.status}`);
    }
    return res.json();
}

async function fetchSubmit(taskId, token) {
    const res = await fetch(`/api/task/${taskId}/fast-choice/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ sessionToken: token }),
    });
    return res.json();
}

async function fetchRoundImages(taskId, roundToken, token) {
    const url = `/api/task/${taskId}/fast-choice/round/${encodeURIComponent(roundToken)}?sessionToken=${encodeURIComponent(token)}`;
    const res = await fetch(url, { method: 'GET', credentials: 'same-origin' });
    if (!res.ok) throw new Error(`round_failed_${res.status}`);
    return res.json();
}

async function fetchAnswer(taskId, roundToken, chosen, token, elapsedMs, answerMs) {
    const res = await fetch(`/api/task/${taskId}/fast-choice/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ sessionToken: token, token: roundToken, chosen, elapsedMs, answerMs }),
    });
    if (!res.ok) throw new Error(`answer_failed_${res.status}`);
    return res.json();
}

function prefetchRoundImages(taskId, token) {
    if (!state || state.prefetched.has(token)) return;
    state.prefetched.add(token);
    fetchRoundImages(taskId, token, sessionToken)
        .then((data) => {
            if (!state) return;
            state.imagesByToken.set(token, data.images || []);
            (data.images || []).forEach((url) => {
                if (url) {
                    const img = new Image();
                    img.src = url;
                }
            });
        })
        .catch(() => { state?.prefetched.delete(token); });
}

function displayScore() {
    return Math.max(0, state.score);
}

function updateScoreEl(modal) {
    const el = modal.querySelector('#fc-score');
    if (el) el.textContent = String(displayScore());
}

function renderRoundShell(modal) {
    const K = state.rounds.length;
    const i = state.currentRound;

    setHtml(modal, `
        <div class="fc-game" data-fc-phase="question">
            <div class="fc-hud">
                <div class="fc-hud__pill-group fc-hud__pill-group--points">
                    <span class="fc-hud__pill-label">Punkty</span>
                    <span class="fc-hud__pill-value" id="fc-score">${displayScore()}</span>
                </div>
                <div class="fc-hud__right">
                    <div class="fc-hud__pill-group fc-hud__pill-group--question">
                        <span class="fc-hud__pill-label">Pytanie</span>
                        <span class="fc-hud__pill-value">${i + 1}/${K}</span>
                    </div>
                    <button type="button" class="fc-hud__close js-game-modal-close" aria-label="Zamknij grę">
                        ${closeIconSvg()}
                    </button>
                </div>
            </div>
            <div class="fc-stage" id="fc-stage">
                <div class="fc-timer" id="fc-timer">
                    <span class="fc-timer__value" id="fc-timer-val">--</span>
                </div>
                <p class="fc-question">${escHtml(state.rounds[i].question)}</p>
                <div class="fc-cards" id="fc-cards" aria-hidden="true"></div>
            </div>
        </div>
    `);
}

function setTimerVisual(modal, frac) {
    const el = modal.querySelector('#fc-timer');
    if (!el) return;
    el.classList.remove('fc-timer--warn', 'fc-timer--danger');
    if (frac <= TIMER_DANGER_FRAC) {
        el.classList.add('fc-timer--danger');
    } else if (frac <= TIMER_WARN_FRAC) {
        el.classList.add('fc-timer--warn');
    }
}

function pad2(n) {
    return n < 10 ? `0${n}` : String(n);
}

function phaseQuestion(modal) {
    const i = state.currentRound;
    const round = state.rounds[i];
    const displayMs = round.displaySeconds * 1000;
    const start = performance.now();

    const valEl = modal.querySelector('#fc-timer-val');
    const stage = modal.querySelector('#fc-stage');
    if (stage) stage.dataset.fcPhase = 'question';

    setTimerVisual(modal, 1);

    function tick(now) {
        const elapsed = now - start;
        const remaining = Math.max(0, displayMs - elapsed);
        const sec = Math.ceil(remaining / 1000);
        const frac = displayMs > 0 ? remaining / displayMs : 0;

        if (valEl) valEl.textContent = pad2(sec);
        setTimerVisual(modal, frac);

        if (remaining <= 0) {
            phaseAnswer(modal);
            return;
        }
        raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
}

async function phaseAnswer(modal) {
    const i = state.currentRound;
    const round = state.rounds[i];

    const stage = modal.querySelector('#fc-stage');
    const cardsEl = modal.querySelector('#fc-cards');
    if (stage) stage.dataset.fcPhase = 'answer';
    if (cardsEl) cardsEl.setAttribute('aria-hidden', 'false');

    let images = state.imagesByToken.get(round.token);
    if (!images) {
        try {
            const data = await fetchRoundImages(state.taskId, round.token, sessionToken);
            images = data.images || [];
            state.imagesByToken.set(round.token, images);
        } catch {
            images = [];
        }
    }
    if (!state || state.currentRound !== i) return;

    if (cardsEl) {
        const labelsHtml = round.options.map((opt) => {
            const hasLabel = typeof opt.label === 'string' && opt.label.trim() !== '';
            return `<div class="fc-card-label">${hasLabel ? escHtml(opt.label) : ''}</div>`;
        }).join('');
        const cardsHtml = round.options.map((_opt, idx) => `
                <button class="fc-card" data-fc-idx="${idx}" type="button">
                    <span class="fc-card__media"></span>
                </button>
            `).join('');
        cardsEl.innerHTML = labelsHtml + cardsHtml;

        Array.from(cardsEl.querySelectorAll('.fc-card')).forEach((btn) => {
            const idx = Number(btn.dataset.fcIdx);
            const url = images[idx];
            if (url) {
                const media = btn.querySelector('.fc-card__media');
                if (media) media.style.backgroundImage = `url("${url.replace(/"/g, '\\"')}")`;
            }
            btn.addEventListener('click', () => onCardClick(modal, idx));
        });
    }

    const next = state.rounds[i + 1];
    if (next) prefetchRoundImages(state.taskId, next.token);

    const durMs = state.data.answerSeconds * 1000;
    const start = performance.now();
    state.roundStartedAt = start;

    const valEl = modal.querySelector('#fc-timer-val');
    setTimerVisual(modal, 1);

    function tick(now) {
        const elapsed = now - start;
        const remaining = Math.max(0, durMs - elapsed);
        const sec = Math.ceil(remaining / 1000);
        const frac = durMs > 0 ? remaining / durMs : 0;

        if (valEl) valEl.textContent = pad2(sec);
        setTimerVisual(modal, frac);

        if (remaining <= 0) {
            onTimeout(modal);
            return;
        }
        raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
}

function lockCards(modal) {
    modal.querySelectorAll('.fc-card').forEach((c) => { c.disabled = true; });
}

function attachFeedback(card, isCorrect, bonus) {
    if (!card) return;
    const media = card.querySelector('.fc-card__media');
    if (!media) return;
    const img = document.createElement('img');
    img.className = 'fc-feedback';
    img.alt = '';
    if (isCorrect) {
        img.src = bonus ? imgScorePlus5 : imgScorePlus4;
    } else {
        img.src = imgScoreMinus1;
    }
    media.appendChild(img);
}

function showStreakBadge(modal) {
    const stage = modal.querySelector('#fc-stage');
    if (!stage) return;
    stage.querySelectorAll('.fc-streak').forEach((n) => n.remove());
    const badge = document.createElement('img');
    badge.className = 'fc-streak';
    badge.src = imgSeria;
    badge.alt = '';
    stage.appendChild(badge);
}

async function onCardClick(modal, idx) {
    if (state.locked) return;
    state.locked = true;

    const i = state.currentRound;
    const round = state.rounds[i];
    const chosen = round.options[idx].side;

    if (raf) { cancelAnimationFrame(raf); raf = null; }
    lockCards(modal);

    let isCorrect = false;
    let bonus = false;
    try {
        const elapsedMs = gameStartTime !== null ? Date.now() - gameStartTime : null;
        const answerMs = state.roundStartedAt ? Math.round(performance.now() - state.roundStartedAt) : null;
        const result = await fetchAnswer(state.taskId, round.token, chosen, sessionToken, elapsedMs, answerMs);
        isCorrect = result.correct === true;
        bonus = result.bonus === true;
    } catch {
        isCorrect = false;
        bonus = false;
    }
    if (!state || state.currentRound !== i) return;

    state.score += isCorrect ? (bonus ? 5 : 4) : -1;
    updateScoreEl(modal);

    if (isCorrect) {
        state.streak += 1;
    } else {
        state.streak = 0;
    }

    const card = modal.querySelector(`.fc-card[data-fc-idx="${idx}"]`);
    if (card) {
        card.classList.add(isCorrect ? 'fc-card--correct' : 'fc-card--wrong');
        attachFeedback(card, isCorrect, bonus);
    }

    if (isCorrect && state.streak >= STREAK_THRESHOLD) {
        showStreakBadge(modal);
    }

    timers.push(setTimeout(() => advanceRound(modal), FEEDBACK_LOCK_MS));
}

async function onTimeout(modal) {
    const i = state.currentRound;
    const round = state.rounds[i];

    if (raf) { cancelAnimationFrame(raf); raf = null; }
    state.locked = true;
    lockCards(modal);

    try {
        const elapsedMs = gameStartTime !== null ? Date.now() - gameStartTime : null;
        await fetchAnswer(state.taskId, round.token, null, sessionToken, elapsedMs, null);
    } catch { /* ignore */ }
    if (!state || state.currentRound !== i) return;

    state.score -= 1;
    state.streak = 0;
    updateScoreEl(modal);

    const stage = modal.querySelector('#fc-stage');
    if (stage) {
        const toast = document.createElement('div');
        toast.className = 'fc-toast';
        const im = document.createElement('img');
        im.src = imgScoreMinus1;
        im.alt = '';
        toast.appendChild(im);
        stage.appendChild(toast);
    }

    timers.push(setTimeout(() => advanceRound(modal), TIMEOUT_LOCK_MS));
}

function advanceRound(modal) {
    state.currentRound++;
    state.locked = false;
    if (state.currentRound >= state.rounds.length) {
        submitAndShowResults(modal);
        return;
    }
    renderRoundShell(modal);
    phaseQuestion(modal);
}

async function submitAndShowResults(modal) {
    setHtml(modal, `<div class="fc-game"><div class="fc-pending">Zapisywanie wyniku…</div></div>`);
    let result = null;
    try {
        result = await fetchSubmit(state.taskId, sessionToken);
    } catch (e) {
        result = { reason: 'network_error' };
    }
    if (state?.onComplete) state.onComplete(result);
    document.dispatchEvent(new CustomEvent('game-modal:result-show', {
        detail: {
            rankingPoints: result.rankingPointsEarned ?? 0,
            redeemablePoints: result.redeemablePointsEarned ?? 0,
        },
    }));
}

export async function renderFastChoice(modal, data, onComplete) {
    clearTimers();
    modal.classList.add('game-modal--fast-choice');

    const taskId = data?.task?.id;
    if (!taskId) {
        setHtml(modal, `<div class="fc-game"><div class="fc-error">Brak ID zadania.</div></div>`);
        return;
    }

    setHtml(modal, `<div class="fc-game"><div class="fc-pending">Ładowanie gry…</div></div>`);

    let startData = null;
    try {
        startData = await fetchStart(taskId);
    } catch (e) {
        const m = String(e?.message);
        if (m === 'already_completed' || m === 'game_in_progress') {
            renderGameMessage(modal, startBlockedMessage(m));
        } else {
            setHtml(modal, `<div class="fc-game"><div class="fc-error">Nie udało się wczytać gry.</div></div>`);
        }
        return;
    }

    currentTaskId = taskId;
    sessionToken  = startData.sessionToken ?? null;
    gameStartTime = Date.now();

    state = {
        taskId,
        data: startData,
        rounds: startData.rounds,
        currentRound: 0,
        score: 0,
        streak: 0,
        roundStartedAt: 0,
        locked: false,
        imagesByToken: new Map(),
        prefetched: new Set(),
        onComplete: onComplete || null,
    };

    if (startData.rounds[0]) prefetchRoundImages(taskId, startData.rounds[0].token);

    renderRoundShell(modal);
    phaseQuestion(modal);
}

export async function forceEndFastChoice() {
    if (!currentTaskId || !sessionToken) return;
    const token = sessionToken;
    const taskId = currentTaskId;
    try {
        const res = await fetch(`/api/task/${taskId}/fast-choice/abandon`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ sessionToken: token }),
        });
        if (res.ok) {
            const data = await res.json();
            document.dispatchEvent(new CustomEvent('game-modal:result-show', {
                detail: {
                    rankingPoints: data.rankingPointsEarned ?? 0,
                    redeemablePoints: data.redeemablePointsEarned ?? 0,
                },
            }));
        }
    } catch { /* ignore */ }
}

export function teardownFastChoice(modal) {
    clearTimers();
    state         = null;
    sessionToken  = null;
    currentTaskId = null;
    gameStartTime = null;
    if (modal) modal.classList.remove('game-modal--fast-choice');
}
